import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import { formatDualMoney } from "../../../utils/currency";
import {
  getProductBadgeOption,
  normalizeProductBadgesForSave,
} from "../../../constants/productBadgeOptions";
import ProductBadgeIcon from "../shared/ProductBadgeIcon";
import {
  getNutritionOption,
  normalizeNutritionValuesForSave,
} from "../../../constants/nutritionOptions";
import {
  getZoneCountryFlagUrl,
  getZoneCountryLabel,
} from "../../../constants/zoneCountryOptions";
import "../../../i18n";
import "./AddCategory.css";
import "../shared/ProductMetaDisplay.css";
import "../shared/NutritionValuesEditor.css";

const photoUrl = (photo) => `${config.UPLOADS_SERVER}/product/${photo}`;

const pickLocalized = (details, lang, field, fallback = "") => {
  const list = Array.isArray(details) ? details : [];
  const match = list.find((d) => d?.language === lang && String(d?.[field] || "").trim());
  if (match?.[field]) return match[field];
  const en = list.find((d) => d?.language === "en" && String(d?.[field] || "").trim());
  if (en?.[field]) return en[field];
  const first = list.find((d) => String(d?.[field] || "").trim());
  return first?.[field] || fallback;
};

const ProductDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const languages = useMemo(() => getMenuLanguages(t), [t]);
  const uiLang = String(i18n.language || "en").split("-")[0].toLowerCase();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLangs, setOpenLangs] = useState({});
  const [activePhoto, setActivePhoto] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(
          `${config.dev}/v2/products/retrieve/${id}?trackOpen=false`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("tokenLogin")}` } }
        );
        if (cancelled) return;
        const data = response?.data || {};
        setProduct(data);
        setOpenLangs({});
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));
  const usedLangs = languages.filter((lang) =>
    (product?.productDetails || []).some(
      (d) =>
        d.language === lang.code &&
        String(d.productTitle || d.productDescription || "").trim()
    )
  );
  const langsToShow = usedLangs.length
    ? usedLangs
    : languages.filter((lang) => lang.code === "en");

  const photos = Array.isArray(product?.photos) ? product.photos.filter(Boolean) : [];
  const heroPhoto = activePhoto && photos.includes(activePhoto) ? activePhoto : photos[0];
  const badges = normalizeProductBadgesForSave(product?.productBadges, product?.productBadge);
  const nutrition = normalizeNutritionValuesForSave(product?.nutritionValues);
  const tags = Array.isArray(product?.tags) ? product.tags.filter(Boolean) : [];
  const choices = Array.isArray(product?.choices) ? product.choices.filter(Boolean) : [];
  const hiddenByChoice = useMemo(() => {
    const map = {};
    (product?.hiddenChoiceItems || []).forEach((row) => {
      const cid = String(row?.choice?._id || row?.choice || "");
      if (!cid) return;
      map[cid] = new Set((row.items || []).map((item) => String(item?._id || item)));
    });
    return map;
  }, [product]);

  const fallbackTitle = pickLocalized(product?.productDetails, "en", "productTitle", t("productDetails"));

  const renderDescription = (html) => {
    const cleaned = DOMPurify.sanitize(html || "");
    const text = cleaned.replace(/<[^>]+>/g, "").trim();
    if (!text) return null;
    return <div className="catform__prose" dangerouslySetInnerHTML={{ __html: cleaned }} />;
  };

  const priceFact = product?.haveMultipleSize
    ? t("haveMultipleSizes")
    : formatDualMoney(product?.price, product?.secondaryPrice);

  const badgeLabel = (type, value) => {
    if (type === "customized" && value) return value;
    if (type === "zone" && value) return getZoneCountryLabel(value, uiLang);
    return getProductBadgeOption(type).label;
  };

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("productDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (!product?._id) {
    return (
      <>
        <PageTitle activeMenu={t("productDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
          <div className="catform__actions">
            <button type="button" className="btn catform__btn catform__btn--cancel" onClick={() => navigate("/products")}>
              {t("productsList")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("productDetails")} motherMenu={t("kitchen")} />

      <div className="catform catform--sheet">
        <div className="catform__top catform__identity">
          <button
            type="button"
            className={`catform__cover${heroPhoto ? "" : " is-empty"}`}
            onClick={() => heroPhoto && setLightbox(heroPhoto)}
            aria-label={heroPhoto ? t("prodFormPhoto") : t("prodSheetNoPhoto")}
          >
            {heroPhoto ? <img src={photoUrl(heroPhoto)} alt="" /> : <i className="fas fa-camera" aria-hidden />}
          </button>
          <div className="catform__identity-copy">
            <p className="catform__kicker">{t("prodSheetSubtitle")}</p>
            <h1 className="catform__title">{fallbackTitle}</h1>
            <div className="catform__facts">
              <span className="catform__fact">{priceFact}</span>
              <span className="catform__fact">
                {choices.length} {t("prodFormChoices").toLowerCase()}
              </span>
              {product.imageDisabled && <span className="catform__fact">{t("disableImage")}</span>}
            </div>
          </div>
          <div className="catform__top-actions">
            <button type="button" className="btn catform__link" onClick={() => navigate("/products")}>
              <i className="fas fa-arrow-left" aria-hidden /> {t("productsList")}
            </button>
            <Link to={`/edit-product/${id}`} className="btn catform__btn catform__btn--edit">
              <i className="fas fa-pen" aria-hidden />
              {t("edit")}
            </Link>
          </div>
        </div>

        {photos.length > 1 && (
          <section className="catform__section">
            <div className="catform__thumbs">
              {photos.map((photo) => (
                <button
                  key={photo}
                  type="button"
                  className={`catform__thumb-btn${photo === heroPhoto ? " is-on" : ""}`}
                  onClick={() => setActivePhoto(photo)}
                >
                  <img src={photoUrl(photo)} alt="" />
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="catform__section is-open">
          <h2 className="catform__heading">{t("catSheetLanguages")}</h2>
          <p className="catform__help">{t("sheetLangHint")}</p>
          <div className="catform__pills">
            {langsToShow.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill is-filled ${isLangOpen(lang.code) ? "is-open" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                <i className="fas fa-check" aria-hidden />
              </button>
            ))}
          </div>
          <div className="catform__lang-fields">
            {langsToShow
              .filter((lang) => isLangOpen(lang.code))
              .map((lang) => {
                const title = pickLocalized(product.productDetails, lang.code, "productTitle", t("noTitle"));
                const descriptionNode = renderDescription(
                  pickLocalized(product.productDetails, lang.code, "productDescription")
                );
                return (
                  <div className="catform__lang-panel" key={lang.code} dir={lang.code === "ar" ? "rtl" : undefined}>
                    <div className="catform__field">
                      <span className="catform__label">
                        <img src={lang.flag} alt="" />
                        {lang.label} — {t("title")}
                      </span>
                      <p className="catform__readonly">{title}</p>
                    </div>
                    {descriptionNode && (
                      <div className="catform__field">
                        <span className="catform__label">{t("description")}</span>
                        {descriptionNode}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">{t("prodFormPrice")}</h2>
          {product.haveMultipleSize ? (
            <>
              <p className="catform__help mb-2">{t("haveMultipleSizes")}: {t("yes")}</p>
              <div className="catform__size-chips">
                {(product.extraSizes || []).map((size, index) => (
                  <span key={`${size.size}-${index}`} className="catform__chip">
                    {size.size || "—"} · {formatDualMoney(size.finalPrice, size.secondaryFinalPrice)}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <p className="catform__readonly catform__readonly--price">
              {formatDualMoney(product.price, product.secondaryPrice)}
            </p>
          )}
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">
            {t("prodFormDetails")} <span className="catform__optional">{t("optional")}</span>
          </h2>

          {badges.length > 0 && (
            <div className="catform__meta-block">
              <span className="catform__label">{t("productBadgesLabel")}</span>
              <div className="product-meta-display__chips" style={{ justifyContent: "flex-start" }}>
                {badges.map((item) => (
                  <span key={`${item.type}-${item.value || ""}`} className="product-meta-chip">
                    {item.type === "zone" && item.value ? (
                      <img src={getZoneCountryFlagUrl(item.value)} alt="" className="product-meta-chip__flag" />
                    ) : (
                      <ProductBadgeIcon item={item} className="product-meta-chip__icon" />
                    )}
                    <span className="product-meta-chip__text">{badgeLabel(item.type, item.value)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {nutrition.length > 0 && (
            <div className="catform__meta-block">
              <span className="catform__label">{t("productNutritionLabel")}</span>
              <div className="nutrition-values-summary product-meta-display__chips" style={{ justifyContent: "flex-start" }}>
                {nutrition.map((item) => {
                  const option = getNutritionOption(item.type);
                  return (
                    <span key={item.type} className="nutrition-values-summary-chip">
                      <span className="nutrition-values-summary-chip__icon" aria-hidden>
                        {option.icon}
                      </span>
                      <span className="nutrition-values-summary-chip__text">
                        {option.label}
                        {item.value ? ` · ${item.value}` : ""}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {tags.length > 0 && (
            <div className="catform__meta-block">
              <span className="catform__label">{t("tags") || "Tags"}</span>
              <div className="catform__chips">
                {tags.map((tag) => (
                  <span key={tag} className="catform__chip">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {badges.length === 0 && nutrition.length === 0 && tags.length === 0 && (
            <p className="catform__muted">{t("prodSheetNoDetails")}</p>
          )}
        </section>

        <section className="catform__section is-open">
          <h2 className="catform__heading">
            {t("prodFormChoices")}
            {choices.length > 0 && <span className="catform__fold-count">{choices.length}</span>}
            <span className="catform__optional">{t("optional")}</span>
          </h2>

          <div className="prod-choices__list catform__sheet-choices">
            {choices.length === 0 ? (
              <div className="prod-choices__empty">
                <i className="fas fa-layer-group" aria-hidden />
                <p>{t("prodSheetNoChoices")}</p>
              </div>
            ) : (
              choices.map((choice) => {
                const choiceId = String(choice._id);
                const items = Array.isArray(choice.items) ? choice.items.filter((item) => item && !item.isArchived) : [];
                const hidden = hiddenByChoice[choiceId] || new Set();
                const keptCount = items.filter((item) => !hidden.has(String(item._id))).length;
                return (
                  <article
                    key={choiceId}
                    className={`prod-choice is-checked${items.length > 0 ? " is-open" : ""}`}
                  >
                    <div className="prod-choice__head">
                      <span className="prod-choice__name">
                        {pickLocalized(choice.choiceDetails, uiLang, "choiceTitle", t("unnamedChoice", { defaultValue: "Unnamed choice" }))}
                      </span>
                      {items.length > 0 && (
                        <span className="prod-choice__count">
                          {keptCount}/{items.length}
                        </span>
                      )}
                    </div>
                    {items.length > 0 && (
                      <div className="prod-choice__body">
                        {items.map((item) => {
                          const hiddenItem = hidden.has(String(item._id));
                          return (
                            <div
                              key={item._id}
                              className={`prod-choice__item${hiddenItem ? "" : " is-checked"}`}
                            >
                              <span className={`prod-choice__item-name${hiddenItem ? " is-muted" : ""}`}>
                                {pickLocalized(item.itemDetails, uiLang, "itemTitle", "—")}
                                {hiddenItem ? ` · ${t("prodSheetHidden")}` : ""}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </section>

        <div className="catform__actions">
          <button type="button" className="btn catform__btn catform__btn--cancel" onClick={() => navigate("/products")}>
            {t("productsList")}
          </button>
          <Link to={`/edit-product/${id}`} className="btn catform__btn catform__btn--edit">
            <i className="fas fa-pen" aria-hidden />
            {t("edit")}
          </Link>
        </div>
      </div>

      {lightbox && (
        <div className="catform-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button type="button" className="catform-lightbox__close" aria-label={t("cancel")}>
            <i className="fas fa-times" />
          </button>
          <img src={photoUrl(lightbox)} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default ProductDetails;
